import { MAX_CHILDREN_PER_PARENT } from './stageNumbering'

export interface ApiStageRow {
  id: number
  stageId: number
  index: number
  completed?: boolean
  startedAt?: string | null
  parentId?: number | null
  description?: string
  observation?: string
  executionTime?: number | null
  setupTime?: number | null
  Stage?: { id: number; name: string }
  stage?: { id: number; name: string }
  Children?: ApiStageRow[]
  FileInServiceStage?: {
    GeneralFile: { id: number; title?: string; originalName?: string; fileName?: string; fileUrl?: string }
  }[]
  Attachments?: any[]
}

export interface FormServiceItem {
  rowKey: string
  id: number | string
  serviceStageId?: number
  serviceId: number
  service: string
  description: string
  observation: string
  executionTime?: number | null
  setupTime?: number | null
  existingFiles?: { id: number; name: string; url: string }[]
  pendingFiles?: File[]
  pendingGalleryFiles?: { id: number; name: string; url?: string }[]
  fromProduct?: boolean
  children?: FormServiceItem[]
  parentDbId?: number | null
  completed?: boolean
  startedAt?: string | null
}

export interface StagePayloadRow {
  id?: number
  stageId: number
  index: number
  parentId?: number | null
  parentTempKey?: string
  tempKey?: string
  description?: string
  observation?: string
  executionTime?: number | null
  setupTime?: number | null
}

export function mapApiStageToFormItem(
  stage: ApiStageRow,
  rowKey?: string,
): FormServiceItem {
  const stageMeta = stage.Stage ?? stage.stage
  const rawFiles = stage.FileInServiceStage || stage.Attachments || []
  return {
    rowKey: rowKey ?? `stage-${stage.id}`,
    id: stage.id,
    serviceStageId: stage.id,
    serviceId: stage.stageId,
    service: stageMeta?.name ?? '',
    description: stage.description || '',
    observation: stage.observation || '',
    executionTime: stage.executionTime ?? null,
    setupTime: stage.setupTime ?? null,
    parentDbId: stage.parentId ?? null,
    existingFiles: rawFiles.map((file: any) => {
      const gf = file.GeneralFile || file
      return {
        id: gf.id,
        name: gf.title || gf.originalName || gf.fileName || 'Anexo',
        url: gf.fileUrl || '',
      }
    }),
    pendingFiles: [],
    pendingGalleryFiles: [],
    completed: stage.completed ?? false,
    startedAt: stage.startedAt ?? null,
    children: (stage.Children ?? []).map((child) =>
      mapApiStageToFormItem(child),
    ),
  }
}

export function apiRootsToFormTree(roots: ApiStageRow[]): FormServiceItem[] {
  if (!roots?.length) return []
  const hasChildren = roots.some((r) => (r.Children?.length ?? 0) > 0)
  if (hasChildren) {
    return roots.map((r) => mapApiStageToFormItem(r))
  }
  const onlyRoots = roots.filter((r) => r.parentId == null)
  if (onlyRoots.length === roots.length) {
    return onlyRoots.map((r) => mapApiStageToFormItem(r))
  }
  const rootRows = roots.filter((r) => r.parentId == null)
  return rootRows.map((root) => ({
    ...mapApiStageToFormItem(root),
    children: roots
      .filter((r) => r.parentId === root.id)
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((child) => mapApiStageToFormItem(child)),
  }))
}

export type StageDisplayRow = {
  stage: ApiStageRow
  parentIndex: number
  childIndex?: number
  isChild: boolean
}

/** Lista depth-first para tabelas/modais de visualização (raiz → filhos). */
export function flattenStagesForDisplay(stages: ApiStageRow[]): StageDisplayRow[] {
  if (!stages?.length) return []

  const hasNestedChildren = stages.some((s) => (s.Children?.length ?? 0) > 0)
  if (hasNestedChildren) {
    return stages.flatMap((root, parentIndex) => [
      { stage: root, parentIndex, isChild: false },
      ...(root.Children ?? [])
        .slice()
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((child, childIndex) => ({
          stage: child,
          parentIndex,
          childIndex,
          isChild: true,
        })),
    ])
  }

  const roots = stages.filter((s) => s.parentId == null)
  if (roots.length < stages.length) {
    return roots.flatMap((root, parentIndex) => [
      { stage: root, parentIndex, isChild: false },
      ...stages
        .filter((s) => s.parentId === root.id)
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((child, childIndex) => ({
          stage: child,
          parentIndex,
          childIndex,
          isChild: true,
        })),
    ])
  }

  return stages.map((stage, parentIndex) => ({
    stage,
    parentIndex,
    isChild: false,
  }))
}

export function formTreeToStagesPayload(
  services: FormServiceItem[],
): StagePayloadRow[] {
  const rows: StagePayloadRow[] = []
  let globalIndex = 0
  services.forEach((parent, parentIndex) => {
    const tempKey = `p-${parentIndex}`
    const parentRowId = parent.serviceStageId ?? parent.id
    const isPersistedParent =
      typeof parentRowId === 'number' &&
      parentRowId > 0 &&
      parentRowId < 1_000_000_000

    rows.push({
      ...(isPersistedParent ? { id: parentRowId as number } : {}),
      stageId: parseInt(String(parent.serviceId), 10),
      index: globalIndex++,
      parentId: null,
      tempKey,
      description: parent.description || '',
      observation: parent.observation || '',
      executionTime: parent.executionTime ?? null,
      setupTime: parent.setupTime ?? null,
    })
    ;(parent.children ?? []).forEach((child) => {
      const childRowId = child.serviceStageId ?? child.id
      const isPersistedChild =
        typeof childRowId === 'number' &&
        childRowId > 0 &&
        childRowId < 1_000_000_000

      rows.push({
        ...(isPersistedChild ? { id: childRowId as number } : {}),
        stageId: parseInt(String(child.serviceId), 10),
        index: globalIndex++,
        parentTempKey: tempKey,
        parentId: parent.parentDbId ?? undefined,
        description: child.description || '',
        observation: child.observation || '',
        executionTime: child.executionTime ?? null,
        setupTime: child.setupTime ?? null,
      })
    })
  })
  return rows
}

export function flattenFormServices(services: FormServiceItem[]): FormServiceItem[] {
  const flat: FormServiceItem[] = []
  for (const root of services) {
    flat.push(root)
    for (const child of root.children ?? []) {
      flat.push(child)
    }
  }
  return flat
}

export function findInvalidServiceStage(
  services: FormServiceItem[],
): FormServiceItem | null {
  return (
    flattenFormServices(services).find(
      (s) => !s.serviceId || s.serviceId <= 0,
    ) ?? null
  )
}

export function canAddChild(parent: FormServiceItem): boolean {
  return (parent.children?.length ?? 0) < MAX_CHILDREN_PER_PARENT
}

export function getNextStageNameFromRoots(roots: ApiStageRow[]): string {
  const sorted = [...roots].sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
  for (const root of sorted) {
    if (!root.completed) {
      return root.Stage?.name ?? root.stage?.name ?? 'Estágio indefinido'
    }
    const children = [...(root.Children ?? [])].sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0),
    )
    const incompleteChild = children.find((c) => !c.completed)
    if (incompleteChild) {
      return (
        incompleteChild.Stage?.name ??
        incompleteChild.stage?.name ??
        'Estágio indefinido'
      )
    }
  }
  return 'Todos concluídos'
}

export function findStageInApiTree(
  roots: ApiStageRow[],
  parentIndex: number,
  childIndex?: number,
): ApiStageRow | undefined {
  const parent = roots[parentIndex]
  if (!parent) return undefined
  if (childIndex === undefined) return parent
  const children = [...(parent.Children ?? [])].sort(
    (a, b) => (a.index ?? 0) - (b.index ?? 0),
  )
  return children[childIndex]
}

export function reorderParentServices(
  services: FormServiceItem[],
  parentIndex: number,
  direction: 'up' | 'down',
): FormServiceItem[] {
  const next = [...services]
  const newIndex = direction === 'up' ? parentIndex - 1 : parentIndex + 1
  if (newIndex < 0 || newIndex >= next.length) return services
  if (next[newIndex].completed) return services
  ;[next[parentIndex], next[newIndex]] = [next[newIndex], next[parentIndex]]
  return next
}

export function reorderChildServices(
  services: FormServiceItem[],
  parentIndex: number,
  childIndex: number,
  direction: 'up' | 'down',
): FormServiceItem[] {
  return services.map((parent, pi) => {
    if (pi !== parentIndex) return parent
    const children = [...(parent.children ?? [])]
    const newIndex = direction === 'up' ? childIndex - 1 : childIndex + 1
    if (newIndex < 0 || newIndex >= children.length) return parent
    if (children[newIndex].completed) return parent
    ;[children[childIndex], children[newIndex]] = [
      children[newIndex],
      children[childIndex],
    ]
    return { ...parent, children }
  })
}

export function addChildToParent(
  services: FormServiceItem[],
  parentIndex: number,
  child: FormServiceItem,
): FormServiceItem[] {
  return services.map((parent, pi) => {
    if (pi !== parentIndex) return parent
    return {
      ...parent,
      children: [...(parent.children ?? []), child],
    }
  })
}

export function removeServiceByRowKey(
  services: FormServiceItem[],
  rowKey: string,
): FormServiceItem[] {
  return services
    .filter((parent) => parent.rowKey !== rowKey)
    .map((parent) => ({
      ...parent,
      children: (parent.children ?? []).filter((c) => c.rowKey !== rowKey),
    }))
}

export function updateServiceByRowKey(
  services: FormServiceItem[],
  rowKey: string,
  updater: (item: FormServiceItem) => FormServiceItem,
): FormServiceItem[] {
  return services.map((parent) => {
    if (parent.rowKey === rowKey) {
      return updater(parent)
    }
    if (!parent.children?.length) {
      return parent
    }
    return {
      ...parent,
      children: parent.children.map((child) =>
        child.rowKey === rowKey ? updater(child) : child,
      ),
    }
  })
}

const SERVICES_CLIPBOARD_TYPE = 'dustre:production-services'

export interface ClipboardServiceNode {
  serviceId: number
  service: string
  description: string
  observation: string
  executionTime?: number | null
  setupTime?: number | null
  children?: ClipboardServiceNode[]
}

export interface ServicesClipboardPayload {
  type: typeof SERVICES_CLIPBOARD_TYPE
  version: 1
  services: ClipboardServiceNode[]
}

/** Serializa a árvore de serviços do front para o formato da área de transferência. */
export function serviceTreeToClipboard(
  services: FormServiceItem[],
): ServicesClipboardPayload {
  const mapNode = (item: FormServiceItem): ClipboardServiceNode => ({
    serviceId: item.serviceId,
    service: item.service || '',
    description: item.description || '',
    observation: item.observation || '',
    executionTime: item.executionTime ?? null,
    setupTime: item.setupTime ?? null,
    children: (item.children ?? []).map(mapNode),
  })

  return {
    type: SERVICES_CLIPBOARD_TYPE,
    version: 1,
    services: services.map(mapNode),
  }
}

export function isServicesClipboardPayload(
  value: unknown,
): value is ServicesClipboardPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as ServicesClipboardPayload).type === SERVICES_CLIPBOARD_TYPE &&
    Array.isArray((value as ServicesClipboardPayload).services)
  )
}

/**
 * Converte o conteúdo colado em novos itens de serviço do front, gerando chaves
 * novas e descartando qualquer vínculo com etapas já persistidas (ids, arquivos,
 * estado de conclusão).
 */
export function clipboardToServiceTree(
  payload: ServicesClipboardPayload,
): FormServiceItem[] {
  const makeRowKey = (prefix: string) =>
    `${prefix}-${Date.now()}-${Math.random()}`

  const mapNode = (
    node: ClipboardServiceNode,
    isChild: boolean,
  ): FormServiceItem => ({
    rowKey: makeRowKey(isChild ? 'child' : 'row'),
    id: Date.now() + Math.random(),
    serviceId: Number(node.serviceId) || 0,
    service: node.service || '',
    description: node.description || '',
    observation: node.observation || '',
    executionTime: node.executionTime ?? null,
    setupTime: node.setupTime ?? null,
    existingFiles: [],
    pendingFiles: [],
    pendingGalleryFiles: [],
    children: isChild
      ? []
      : (node.children ?? [])
          .slice(0, MAX_CHILDREN_PER_PARENT)
          .map((child) => mapNode(child, true)),
  })

  return (payload.services ?? []).map((node) => mapNode(node, false))
}

export function collectPendingFilesByPosition(services: FormServiceItem[]) {
  return services.flatMap((parent, parentIndex) => [
    {
      parentIndex,
      childIndex: undefined as number | undefined,
      files: parent.pendingFiles || [],
      galleryFiles: parent.pendingGalleryFiles || [],
    },
    ...(parent.children ?? []).map((child, childIndex) => ({
      parentIndex,
      childIndex,
      files: child.pendingFiles || [],
      galleryFiles: child.pendingGalleryFiles || [],
    })),
  ])
}
